import { CustomAxiosHeaders } from '../config/services'
import { EmailBuilder } from '../models'
import { requests } from '.'

// use serviceName value below as key in `versions` object in `src/config/services.ts` for developer versions
const serviceName = 'EmailBuilderService'
const opts: CustomAxiosHeaders = { serviceName }
const request = (addlOpts?: CustomAxiosHeaders) => {
  return requests(Object.assign({}, opts, addlOpts))
}

const EmailBuilderServices = {
  migrateBulkRequests: locationId =>
    request().get(`/emails/schedule/migrate/${locationId}`),
  getById: async ({
    templateId,
    locationId,
  }: {
    templateId: string
    locationId: string
  }) => {
    let reqConfig = {
      params: { fields: 'versionHistory,emailData' },
    }
    try {
      const axiosResponse = await request().get(
        `/emails/builder/${locationId}/${templateId}`,
        reqConfig
      )
      return new EmailBuilder(axiosResponse.data)
    } catch (err) {
      throw err
    }
  },

  getByLocationId: async ({ locationId, builderVersion = '' }) => {
    let reqConfig = {
      params: {
        locationId,
        deleted: false,
        fields:
          'versionHistory,builderVersion,fromAddress,fromName,subjectLine,previewText,locationId,type,id,archived,emailData,downloadUrl,dateAdded,dateUpdated',
      },
    }
    if (builderVersion) {
      reqConfig.params['builderVersion'] = builderVersion
    }
    try {
      const axiosResponse = await request().get(
        `/emails/builder/all`,
        reqConfig
      )
      return axiosResponse.data
    } catch (err) {
      throw err
    }
  },

  getByOriginId: async ({
    locationId,
    originId,
  }: {
    locationId: string
    originId: string
  }) => {
    let reqConfig = {
      params: {
        locationId,
        deleted: false,
        originId,
        fields: 'versionHistory',
      },
    }
    try {
      const axiosResponse = await request().get(
        `/emails/builder/all`,
        reqConfig
      )
      if (!axiosResponse.data || axiosResponse?.data?.length === 0) {
        return undefined
      } else {
        return new EmailBuilder(axiosResponse.data[0])
      }
    } catch (err) {
      throw err
    }
  },

  delete: async ({ locationId, templateId }: { locationId: string, templateId: string }) => {
    try {
      const axiosResponse = await request().delete(
        `/emails/builder/${locationId}/${templateId}`
      )
      return axiosResponse.data
    } catch (err) {
      throw err
    }
  },

  create: async({
    locationId,
    type,
    originId = null,
    name,
    builderVersion = '2'
  }) => {
    const params = {
      locationId: locationId,
      type: type,
      builderVersion,
    }
    if (originId) {
      params['originId'] = originId
    }
    if (name) {
      params['name'] = name
    }
    try {
      const axiosResponse = await request().post(`/emails/builder`, params)
      return new EmailBuilder(axiosResponse.data)
    } catch (err) {
      throw err
    }
  },

  updateData: async ({
    locationId,
    templateId,
    payload,
    updatedBy = ''
  }) => {
    const params = {
      locationId: locationId,
      templateId: templateId,
      updatedBy,
      payload,
    }
    try {
      const axiosResponse = await request().post(
        `/emails/builder/old/data`,
        params
      )
      return axiosResponse.data
    } catch (err) {
      throw err
    }
  },

  cloneTemplate: async (params: { [key: string]: any }) => {
    try {
      const axiosResponse = await request().post(
        `/emails/builder/clone`,
        params
      )
      return axiosResponse.data
    } catch (err) {
      throw err
    }
  },

  getWorkflowDetails: ({ locationId, workflowId, actionId, templateId }) => {
    try {
      return request().get(`/emails/campaigns/${locationId}/template`, {
        params: { workflowId, actionId, templateId },
      })
    } catch (err) {
      throw err
    }
  },

  updateWorkflowDetails: ({ locationId, workflowId, actionId, templateId }) => {
    try {
      return request().post(`/emails/campaigns/${locationId}/template`, {
        locationId,
        workflowId,
        actionId,
        templateId,
      })
    } catch (err) {
      throw err
    }
  },
}

export default EmailBuilderServices
